<div class="blog-detail-page">
  <!-- Hero Section (Always visible, shows skeleton when loading) -->
  <section
    class="blog-detail-hero"
    [class.blog-detail-hero--loading]="isLoading"
    [style.background-image]="!isLoading && detail && detail.coverImageUrl ? 'url(' + detail.coverImageUrl + ')' : null"
    >
    <div class="blog-detail-hero__container">
      <!-- Loading: Hero Skeleton -->
      @if (isLoading) {
        <div class="skeleton-hero-content">
          <div class="skeleton skeleton-badge"></div>
          <div class="skeleton skeleton-hero-title"></div>
          <div class="skeleton skeleton-hero-title-short"></div>
          <div class="skeleton skeleton-hero-meta"></div>
          <div class="skeleton skeleton-hero-excerpt"></div>
          <div class="skeleton skeleton-hero-excerpt-short"></div>
        </div>
      }

      <!-- Loaded: Actual Content -->
      @if (!isLoading && detail) {
        <!-- Glassmorphic content with title, categories, meta -->
        <div class="blog-detail-hero__content">
          <!-- Title -->
          <h1 class="blog-detail-hero__title">{{ detail.title }}</h1>
          <!-- Metadata & Categories Row -->
          <div class="blog-detail-hero__meta">
            @if (detail.author) {
              <span class="meta-item">
                <a
                  class="meta-author-link"
                  [routerLink]="['/auteur', detail.author.slug]"
                >
                  <icon name="person" class="meta-icon"></icon>
                  <span class="meta-author-link__name">{{ detail.author.name }}</span>
                </a>
                @if (displayDate) {
                  <span class="meta-date">&nbsp;·&nbsp;{{ displayDate }}</span>
                }
              </span>
            } @else if (authorAndDate; as ad) {
              <span class="meta-item">
                @if (detail.authorName) {
                  <icon name="person" class="meta-icon"></icon>
                }
                {{ ad }}
              </span>
            }
            @if (lastModified; as lm) {
              <span class="meta-item meta-item--modified">
                Laatste wijziging: {{ lm }}
              </span>
            }
            @for (category of detail.categories; track category) {
              <span class="category-chip">
                {{ category.name }}
              </span>
            }
          </div>
        </div>
        <!-- Excerpt -->
        @if (detail.excerpt) {
          <p class="blog-detail-hero__excerpt">{{ detail.excerpt }}</p>
        }
      }
    </div>

    <!-- Wave separator -->
    <svg class="wave-separator" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <path d="M0,40 C240,100 480,0 720,40 C960,80 1200,20 1440,60 L1440,120 L0,120 Z" fill="#f6f6f6"></path>
    </svg>
  </section>

  <!-- Article Content -->
  <div class="blog-detail-content-container">
    <!-- Loading State -->
    @if (isLoading) {
      <div class="blog-detail-loading">
        <!-- Content Skeleton -->
        <div class="skeleton-content">
          <!-- Paragraph skeleton -->
          <div class="skeleton-block">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text-short"></div>
          </div>
          <!-- Heading skeleton -->
          <div class="skeleton skeleton-heading"></div>
          <!-- Paragraph skeleton -->
          <div class="skeleton-block">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text-short"></div>
          </div>
          <!-- Image skeleton -->
          <div class="skeleton skeleton-image"></div>
          <!-- Paragraph skeleton -->
          <div class="skeleton-block">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text-short"></div>
          </div>
          <!-- Heading skeleton -->
          <div class="skeleton skeleton-heading-small"></div>
          <!-- Paragraph skeleton -->
          <div class="skeleton-block">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-text-short"></div>
          </div>
        </div>
      </div>
    }

    <!-- Error State -->
    @if (!isLoading && errorMessage) {
      <app-error-state
        [title]="'Artikel niet gevonden'"
        [message]="errorMessage"
        [buttonText]="'Terug naar overzicht'"
        [iconName]="'error'"
        (buttonClick)="goBack()">
      </app-error-state>
    }

    <!-- Article -->
    @if (!isLoading && detail) {
      <article class="blog-detail-article">
        <!-- Article Content -->
        <div class="article-content">
          @for (block of detail.blocks; track trackBlock($index, block)) {
            @if (block.type === 'heading') {
              <app-blog-block-heading
                [block]="block"
              ></app-blog-block-heading>
            }
            @if (block.type === 'paragraph') {
              <app-blog-block-paragraph
                [block]="block"
              ></app-blog-block-paragraph>
            }
            @if (block.type === 'image') {
              <app-blog-block-image
                [block]="block"
              ></app-blog-block-image>
            }
            @if (block.type === 'link') {
              <app-blog-block-link
                [block]="block"
              ></app-blog-block-link>
            }
            @if (block.type === 'button') {
              <app-blog-block-button
                [block]="block"
              ></app-blog-block-button>
            }
            @if (block.type === 'quote') {
              <app-blog-block-quote
                [block]="block"
              ></app-blog-block-quote>
            }
          }
        </div>
        <!-- Social Share -->
        <div class="article-share-section">
          <h3 class="article-section-title">Deel dit artikel</h3>
          <p class="article-section-subtitle">Vond je dit artikel interessant? Deel het met je netwerk!</p>
          <app-social-links
            [shareUrl]="'https://blauwtand.com/stories/' + detail.slug"
            [shareTitle]="detail.title"
            [shareText]="detail.excerpt || ''"
            email="info@blauwtand.com"
            phone="+31612345678"
            linkedin="https://www.linkedin.com/company/blauwtand"
            whatsapp="+31612345678"
            [showEmail]="true"
            [showPhone]="true"
            [showLinkedIn]="true"
            [showWhatsApp]="true"
            [showFacebook]="true"
            [showTwitter]="true"
            [showCopyLink]="true"
          ></app-social-links>
        </div>
        <!-- Back to Blog -->
        <div class="article-footer">
          <a routerLink="/stories" class="apple-cta apple-cta--blue items-center">
            <span>Terug naar het overzicht</span>
            <icon name="navigate_next" class="translate-y-[2px]"></icon>
          </a>
        </div>
      </article>
    }
  </div>
</div>
